import { Badge } from '@ovComponents/2-component/badge/badge';
import { Pagination } from '@ovComponents/2-component/pagination/pagination';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeadCell,
  TableRow,
} from '@ovComponents/2-component/table/table';
import { Box, Skeleton, Typography } from 'hephaestus/web';
import { useTranslation } from 'react-i18next';
import { useLocalization } from '../../../../hooks/useLocalization';
import type { BulkRebalanceRun } from '../types/run.types';
import { activateOnEnterOrSpace } from '../utils/presentation/activateOnEnterOrSpace';
import {
  formatRunCreatedBy,
  formatRunListOutcomePrimary,
  formatRunListOutcomeSecondaryLines,
  formatRunOrganization,
} from '../utils/presentation/runListPresentation';
import { getRunStateBadgeColor } from '../utils/presentation/runStateBadge';

export type BulkRebalanceRunsTableProps = {
  runs: BulkRebalanceRun[];
  loading: boolean;
  errorMessage?: string;
  page: number;
  pageSize: number;
  totalCount: number;
  onPageChange: (page: number) => void;
  onRunSelect: (runId: string) => void;
};

const TABLE_COLUMN_COUNT = 5;

export const BulkRebalanceRunsTable = ({
  runs,
  loading,
  errorMessage,
  page,
  pageSize,
  totalCount,
  onPageChange,
  onRunSelect,
}: BulkRebalanceRunsTableProps) => {
  const { translateBackendString } = useLocalization();
  const { t } = useTranslation(['bulkRebalance']);
  const { localizedDateTime } = useLocalization();
  const pageCount = Math.max(1, Math.ceil(totalCount / pageSize));

  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', gap: 2 }}>
      <Table sx={{ minWidth: 1080 }} data-testid='bulk-rebalance-runs-table'>
        <TableHead>
          <TableRow>
            <TableHeadCell data-testid='bulk-rebalance-header-created-at'>
              {t('table.createdAt')}
            </TableHeadCell>
            <TableHeadCell data-testid='bulk-rebalance-header-type'>
              {t('table.type')}
            </TableHeadCell>
            <TableHeadCell data-testid='bulk-rebalance-header-organization'>
              {t('table.organization')}
            </TableHeadCell>
            <TableHeadCell data-testid='bulk-rebalance-header-status'>
              {t('table.status')}
            </TableHeadCell>
            <TableHeadCell data-testid='bulk-rebalance-header-result'>
              {t('table.result')}
            </TableHeadCell>
          </TableRow>
        </TableHead>
        <TableBody>
          {loading &&
            Array.from({ length: 3 }).map((_, index) => (
              <TableRow key={`skeleton-${index}`}>
                {Array.from({ length: TABLE_COLUMN_COUNT }).map(
                  (__, cellIndex) => (
                    <TableCell key={`skeleton-cell-${cellIndex}`}>
                      <Skeleton variant='text' width='80%' />
                    </TableCell>
                  ),
                )}
              </TableRow>
            ))}

          {!loading && errorMessage && (
            <TableRow>
              <TableCell colSpan={TABLE_COLUMN_COUNT}>
                <Typography variant='bodyMedium' color='error'>
                  {errorMessage}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!loading && !errorMessage && runs.length === 0 && (
            <TableRow>
              <TableCell colSpan={TABLE_COLUMN_COUNT}>
                <Typography variant='bodyMedium' color='textSecondary'>
                  {t('table.empty')}
                </Typography>
              </TableCell>
            </TableRow>
          )}

          {!loading &&
            !errorMessage &&
            runs.map((run) => {
              const outcomeSecondaryLines = formatRunListOutcomeSecondaryLines(
                t,
                run,
              );
              const createdByLabel = formatRunCreatedBy(run);
              const organizationLabel = formatRunOrganization(
                translateBackendString,
                run,
              );

              return (
                <TableRow
                  hover
                  pointer
                  key={run.id}
                  role='button'
                  tabIndex={0}
                  aria-label={t('table.openRunAria', {
                    when: localizedDateTime(run.createdAt),
                    type: t(`runType.${run.type}`),
                  })}
                  onClick={() => onRunSelect(run.id)}
                  onKeyDown={activateOnEnterOrSpace(() => onRunSelect(run.id))}
                  data-testid={`bulk-rebalance-row-${run.id}`}
                >
                  <TableCell data-testid={`bulk-rebalance-when-${run.id}`}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant='bodyMedium'>
                        {localizedDateTime(run.createdAt)}
                      </Typography>
                      {createdByLabel && (
                        <Typography variant='bodySmall' color='textSecondary'>
                          {t('table.byCreator', { name: createdByLabel })}
                        </Typography>
                      )}
                    </Box>
                  </TableCell>
                  <TableCell data-testid={`bulk-rebalance-type-${run.id}`}>
                    <Badge
                      label={t(`runType.${run.type}`)}
                      color='supportOne'
                    />
                  </TableCell>
                  <TableCell
                    data-testid={`bulk-rebalance-organization-${run.id}`}
                  >
                    <Typography variant='bodyMedium'>
                      {organizationLabel ?? '—'}
                    </Typography>
                  </TableCell>
                  <TableCell data-testid={`bulk-rebalance-status-${run.id}`}>
                    <Badge
                      label={t(`runState.${run.state}`)}
                      color={getRunStateBadgeColor(run.state)}
                      testId={`bulk-rebalance-state-badge-${run.id}`}
                    />
                  </TableCell>
                  <TableCell data-testid={`bulk-rebalance-outcome-${run.id}`}>
                    <Box
                      sx={{
                        display: 'flex',
                        flexDirection: 'column',
                        gap: 0.5,
                      }}
                    >
                      <Typography variant='bodyMedium'>
                        {formatRunListOutcomePrimary(t, run)}
                      </Typography>
                      {outcomeSecondaryLines.map((line) => (
                        <Typography
                          key={line}
                          variant='bodySmall'
                          color='textSecondary'
                        >
                          {line}
                        </Typography>
                      ))}
                    </Box>
                  </TableCell>
                </TableRow>
              );
            })}
        </TableBody>
      </Table>

      {!loading && !errorMessage && totalCount > pageSize && (
        <Box sx={{ display: 'flex', justifyContent: 'flex-end' }}>
          <Pagination
            page={page}
            count={pageCount}
            onChange={(_, nextPage) => onPageChange(nextPage)}
          />
        </Box>
      )}
    </Box>
  );
};
